import { TextField, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { maskValues } from "./maskUtils";

export default function MaskedInput({ field, error, maskType, label, onBlur }) {
  const [showValue, setShowValue] = useState(false);
  const realValueRef = useRef(field.value || "");
  const autoMaskTimer = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (field.value) realValueRef.current = field.value;
  }, [field.value]);

  useEffect(() => {
    if (showValue) {
      if (autoMaskTimer.current) clearTimeout(autoMaskTimer.current);

      autoMaskTimer.current = setTimeout(() => {
        setShowValue(false);
      }, 10000);
    }

    return () => {
      if (autoMaskTimer.current) clearTimeout(autoMaskTimer.current);
    };
  }, [showValue]);

  const moveCursorToEnd = () => {
  const input = inputRef.current;
  if (!input) return;

  const length = input.value.length;
  input.setSelectionRange(length, length);
};

const handleKeyDown = (e) => {
  if (!showValue && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
    e.preventDefault();
  }
};

  const handleChange = useCallback((e) => {
  const input = e.target.value;

  if (input.length === 0) {
    realValueRef.current = "";
    field.onChange("");
    return;
  }

  if (showValue) {
    realValueRef.current = input;
    field.onChange(input);
    return;
  }

  const prevMasked = maskValues[maskType](realValueRef.current);

  if (input.length < prevMasked.length - 1) {
    const rawValue = input.replace(/\*/g, "");
    realValueRef.current = rawValue;
    field.onChange(rawValue);
    return;
  }

  if (input.length - prevMasked.length > 1) {
    realValueRef.current = input.replace(/\*/g, "");
    field.onChange(realValueRef.current);
    return;
  }

  if (input.length < prevMasked.length) {
    realValueRef.current = realValueRef.current.slice(0, -1);
    field.onChange(realValueRef.current);
    return;
  }

  const lastRealChar = input.replace(/\*/g, "").slice(-1);
  realValueRef.current += lastRealChar;
  field.onChange(realValueRef.current);

}, [field, showValue, maskType]);

    const maskedValue = showValue
      ? realValueRef.current
      : maskValues[maskType](realValueRef.current);

  return (
    <TextField
      {...field}
      inputRef={inputRef} 
      value={maskedValue}
      onChange={handleChange}
      onBlur={(e) => {
        field.onBlur?.(e);   // preserve RHF's internal onBlur
        onBlur?.(e);         // call custom onBlur if provided
      }}

      onClick={!showValue ? moveCursorToEnd : undefined}
      onFocus={!showValue ? moveCursorToEnd : undefined}
      onKeyUp={!showValue ? moveCursorToEnd : undefined}
      onKeyDown={handleKeyDown}  

      fullWidth
      label={label}
      error={!!error}
      helperText={error?.message}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => setShowValue((p) => !p)}>
              {showValue ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  );
}

MaskedInput.propTypes = {
  field: PropTypes.object.isRequired,
  error: PropTypes.object,
  maskType: PropTypes.string.isRequired,
  label: PropTypes.any,
  onBlur: PropTypes.func,
};